// src/api/authApi.js
import axios from 'axios';

// ✅ Use the correct base URL for your backend
const API_BASE_URL = 'https://admin.thefoodies.in/api';

class ApiService {
  constructor() {
    // ✅ PRIVATE API - For authenticated requests (with token)
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    // ✅ PUBLIC API - For unauthenticated requests (NO token)
    this.publicApi = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    // ===== PRIVATE API INTERCEPTORS =====
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token && token !== 'undefined' && token !== 'null') {
          config.headers.Authorization = `Bearer ${token}`;
          console.log('🔑 Token attached to request:', config.url);
        } else {
          console.warn('⚠️ No valid token for:', config.url);
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.warn('🔐 Authentication failed. Please login again.');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('isNewUser');
          localStorage.removeItem('profileCompleted');
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
        }
        
        const errorMessage = error.response?.data?.message || 
                           error.response?.data?.error || 
                           error.message || 
                           'Network error occurred';
        return Promise.reject(errorMessage);
      }
    );

    // ===== PUBLIC API INTERCEPTORS =====
    this.publicApi.interceptors.request.use(
      (config) => {
        console.log('🌐 Public API Request:', {
          url: config.url,
          method: config.method,
          data: config.data
        });
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.publicApi.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('❌ Public API Error:', {
          url: error.config?.url,
          status: error.response?.status,
          data: error.response?.data
        });
        
        const errorMessage = error.response?.data?.message || 
                           error.response?.data?.error || 
                           error.message || 
                           'Network error occurred';
        return Promise.reject(errorMessage);
      }
    );
  }

  // ========== AUTH API (PUBLIC - No Token Required) ==========
  
  sendOTP = async (phone) => {
    try {
      const phoneStr = String(phone).trim();
      console.log('📞 Sending OTP for:', phoneStr, 'Type:', typeof phoneStr);
      
      if (!phoneStr || phoneStr.length !== 10) {
        throw new Error('Please enter a valid 10-digit phone number');
      }
      
      console.log('📤 Full URL:', `${this.publicApi.defaults.baseURL}/send-otp`);
      console.log('📤 Request payload:', { phone: phoneStr });
      
      const response = await this.publicApi.post('/send-otp', { 
        phone: phoneStr 
      });
      
      console.log('✅ OTP sent:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Send OTP error:', error);
      
      if (error.response) {
        console.error('❌ Server response:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        });
      }
      
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 500) {
          if (data?.message?.toLowerCase().includes('spam') || 
              data?.message?.toLowerCase().includes('too many')) {
            throw new Error('Too many OTP attempts. Please wait 5 minutes.');
          }
          throw new Error('Server error: ' + (data?.message || 'Please try again later.'));
        } else if (status === 429) {
          throw new Error('Too many OTP requests. Please wait 5 minutes.');
        } else if (status === 400) {
          throw new Error(data?.message || 'Invalid phone number.');
        } else {
          throw new Error(data?.message || 'Failed to send OTP.');
        }
      } else if (error.request) {
        throw new Error('Network error. Please check your connection.');
      } else {
        throw new Error(error.message || 'Failed to send OTP.');
      }
    }
  };

  verifyOTP = async (phone, otp) => {
    try {
      const phoneStr = String(phone).trim();
      const otpStr = String(otp).trim();
      
      console.log('🔐 Verifying OTP for:', phoneStr);
      
      if (!phoneStr || phoneStr.length !== 10) {
        throw new Error('Invalid phone number');
      }
      if (!otpStr || otpStr.length !== 6) {
        throw new Error('Please enter a valid 6-digit OTP');
      }
      
      const response = await this.publicApi.post('/verify-otp', { 
        phone: phoneStr,
        otp: otpStr
      });
      
      if (response.data?.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify({
          id: response.data.user_id,
          phone: phoneStr,
          name: response.data.name || 'User'
        }));
        localStorage.setItem('isNewUser', response.data.is_new_user ? 'true' : 'false');
        console.log('✅ Token saved successfully');
      }
      
      console.log('✅ OTP verified:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Verify OTP error:', error);
      
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 500) {
          throw new Error('Server error. Please try again later.');
        } else if (status === 401 || status === 400) {
          throw new Error(data?.message || 'Invalid OTP. Please try again.');
        } else {
          throw new Error(data?.message || 'Verification failed.');
        }
      }
      throw error;
    }
  };

  // ========== MEAL OFF API (PRIVATE - Token Required) ==========
  
  // ✅ POST - Mark Meal Off (Skip a meal)
  markMealOff = async (subscriptionId, date, mealTime, mealType) => {
    try {
      const type = mealType || 'Veg';
      
      console.log('🔄 POST - Marking meal off:', { 
        subscriptionId, 
        date, 
        meal_time: mealTime, 
        meal_type: type 
      });
      
      // ✅ FIXED: Use /monthly/ instead of /tiffin/monthly/
      const response = await this.api.post(`/tiffin/monthly/${subscriptionId}/meal-off`, {
        date: date,
        meal_time: mealTime,
        meal_type: type
      });
      
      console.log('✅ POST - Meal marked off:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ POST - Mark meal off error:', error);
      throw error;
    }
  };

  // ✅ DELETE - Remove a meal off (undo)
  removeMealOff = async (subscriptionId, date, mealTime, mealType) => {
    try {
      const type = mealType || 'Veg';
      
      console.log('🔄 DELETE - Removing meal off:', { 
        subscriptionId, 
        date, 
        meal_time: mealTime, 
        meal_type: type 
      });
      
      // ✅ FIXED: Use /monthly/ instead of /tiffin/monthly/
      const response = await this.api.delete(`/tiffin/monthly/${subscriptionId}/meal-off`, {
        data: {
          date: date,
          meal_time: mealTime,
          meal_type: type
        }
      });
      
      console.log('✅ DELETE - Meal off removed:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ DELETE - Remove meal off error:', error);
      throw error;
    }
  };

  // ✅ GET - Get all meal offs for a subscription
  getMealOffs = async (subscriptionId) => {
    try {
      console.log('🔄 GET - Fetching meal offs for subscription:', subscriptionId);
      
      // ✅ FIXED: Use /monthly/ instead of /tiffin/monthly/
      const response = await this.api.get(`/tiffin/monthly/${subscriptionId}/meal-offs`);
      
      console.log('✅ GET - Meal offs fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ GET - Meal offs error:', error);
      
      // ✅ If 404, return empty array instead of throwing
      if (error.response?.status === 404) {
        console.warn('⚠️ No meal offs found for subscription:', subscriptionId);
        return { status: true, data: [] };
      }
      throw error;
    }
  };

  // ========== WEEKLY MENU API (PRIVATE - Token Required) ==========
  
  getWeeklyMenu = async () => {
    try {
      console.log('🔄 GET - Fetching weekly menu...');
      const token = localStorage.getItem('token');
      if (!token || token === 'undefined' || token === 'null') {
        throw new Error('Please login first to view menu');
      }
      
      const response = await this.api.get('/tiffin/weekly-menu');
      console.log('✅ GET - Weekly menu raw response:', response.data);
      
      if (response.data?.status === true && response.data?.data) {
        const groupedData = response.data.data;
        const flatArray = [];
        
        Object.keys(groupedData).forEach(day => {
          if (Array.isArray(groupedData[day])) {
            groupedData[day].forEach(item => {
              flatArray.push({
                ...item,
                day: day
              });
            });
          }
        });
        
        console.log('✅ GET - Weekly menu flattened:', flatArray);
        return { 
          status: true, 
          data: flatArray,
          grouped: groupedData
        };
      }
      
      if (Array.isArray(response.data)) {
        return { status: true, data: response.data };
      }
      
      if (Array.isArray(response)) {
        return { status: true, data: response };
      }
      
      return { status: true, data: [] };
    } catch (error) {
      console.error('❌ GET - Weekly menu error:', error);
      throw error;
    }
  };

  // ========== SUBSCRIPTION API (PRIVATE) ==========
  
  createSubscription = async (subscriptionData) => {
    try {
      console.log('🔄 POST - Creating subscription:', subscriptionData);
      const response = await this.api.post('/tiffin/monthly', subscriptionData);
      console.log('✅ POST - Subscription created:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ POST - Create subscription error:', error);
      throw error;
    }
  };

  getSubscriptions = async () => {
    try {
      console.log('🔄 GET - Fetching subscriptions...');
      const response = await this.api.get('/tiffin/monthly');
      console.log('✅ GET - Subscriptions fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ GET - Subscriptions error:', error);
      throw error;
    }
  };

  cancelSubscription = async (subscriptionId) => {
    try {
      console.log('🔄 POST - Cancelling subscription:', subscriptionId);
      const response = await this.api.post(`/tiffin/monthly/${subscriptionId}/cancel`);
      console.log('✅ POST - Subscription cancelled:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ POST - Cancel subscription error:', error);
      throw error;
    }
  };

  // ========== CUSTOM DATE ORDER API (PRIVATE) ==========
  
  placeCustomDateOrder = async (orderData) => {
    try {
      console.log('🔄 POST - Placing custom date order:', orderData);
      const response = await this.api.post('/order/custom-date', orderData);
      console.log('✅ POST - Custom date order placed:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ POST - Custom date order error:', error);
      throw error;
    }
  };

  // ========== USER API (PRIVATE) ==========
  
  getUserProfile = async () => {
    try {
      console.log('🔄 Fetching user profile...');
      const token = localStorage.getItem('token');
      if (!token || token === 'undefined' || token === 'null') {
        throw new Error('Please login first');
      }
      const response = await this.api.get('/user/profile');
      console.log('✅ Profile fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Get profile error:', error);
      throw error;
    }
  };

  updateUserProfile = async (userData) => {
    try {
      console.log('📤 Updating user profile...');
      const token = localStorage.getItem('token');
      if (!token || token === 'undefined' || token === 'null') {
        throw new Error('Please login first');
      }
      if (userData instanceof FormData) {
        const response = await this.api.post('/user/profile/update', userData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        console.log('✅ Profile updated with image:', response.data);
        return response.data;
      } else {
        const response = await this.api.post('/user/profile/update', userData);
        console.log('✅ Profile updated:', response.data);
        return response.data;
      }
    } catch (error) {
      console.error('❌ Update profile error:', error);
      throw error;
    }
  };

  // ✅ DELETE USER ACCOUNT
  deleteUserAccount = async () => {
    try {
      console.log('🗑️ Deleting user account...');
      const token = localStorage.getItem('token');
      if (!token || token === 'undefined' || token === 'null') {
        throw new Error('Please login first');
      }
      
      const response = await this.api.delete('/delete-account');
      console.log('✅ Account deleted:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Delete account error:', error);
      throw error;
    }
  };

  // ✅ LOGOUT USER
  logoutUser = async () => {
    try {
      console.log('📤 Logging out user...');
      const token = localStorage.getItem('token');
      if (token && token !== 'undefined' && token !== 'null') {
        const response = await this.api.post('/logout');
        console.log('✅ Logged out successfully:', response.data);
        return response.data;
      }
      return { success: true };
    } catch (error) {
      console.error('❌ Logout error:', error);
      throw error;
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('isNewUser');
      localStorage.removeItem('profileCompleted');
      localStorage.removeItem('tiffinPhone');
      localStorage.removeItem('tiffinUserLogin');
      console.log('🧹 LocalStorage cleared');
    }
  };

  // ========== PRODUCT API (PUBLIC) ==========
  
  getProducts = async () => {
    try {
      console.log('🔄 Fetching products...');
      const response = await this.publicApi.get('/products');
      console.log('✅ Products fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Fetch products error:', error);
      if (error.response?.status === 401) {
        console.warn('⚠️ Products require authentication, trying with token...');
        try {
          const token = localStorage.getItem('token');
          if (token && token !== 'undefined' && token !== 'null') {
            const response = await this.api.get('/products');
            console.log('✅ Products fetched with auth:', response.data);
            return response.data;
          }
        } catch (authError) {
          console.error('❌ Products still inaccessible:', authError);
        }
      }
      throw error;
    }
  };

  filterProducts = async (filterData) => {
    try {
      console.log('🔄 POST - Filtering products:', filterData);
      try {
        const response = await this.publicApi.post('/products/filter', filterData);
        return response.data;
      } catch (error) {
        if (error.response?.status === 401) {
          const response = await this.api.post('/products/filter', filterData);
          return response.data;
        }
        throw error;
      }
    } catch (error) {
      console.error('❌ Filter products error:', error);
      throw error;
    }
  };

  // ========== CART API (PRIVATE) ==========
  
  // ✅ POST - Add to Cart
  addToCart = async (cartData) => {
    try {
      console.log('🔄 POST - Adding to cart:', cartData);
      const response = await this.api.post('/cart/add', cartData);
      console.log('✅ POST - Added to cart:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ POST - Add to cart error:', error);
      throw error;
    }
  };

  // ✅ GET - Fetch cart items
  getCartItems = async () => {
    try {
      console.log('🔄 GET - Fetching cart items...');
      const response = await this.api.get('/cart');
      console.log('✅ GET - Cart items fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ GET - Fetch cart error:', error);
      throw error;
    }
  };

  // ✅ POST - Update Cart
  updateCart = async (updateData) => {
    try {
      console.log('🔄 POST - Updating cart:', updateData);
      const response = await this.api.post('/cart/update', updateData);
      console.log('✅ POST - Cart updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ POST - Update cart error:', error);
      throw error;
    }
  };

  // ✅ DELETE - Remove from cart
  removeFromCart = async (removeData) => {
    try {
      console.log('🔄 DELETE - Removing from cart:', removeData);
      const response = await this.api.delete('/cart/remove', {
        data: removeData
      });
      console.log('✅ DELETE - Removed from cart:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ DELETE - Remove from cart error:', error);
      throw error;
    }
  };

  // ✅ POST - Place Order (Using /checkout endpoint)
  placeOrder = async (orderData) => {
    try {
      // ✅ Get cart items from orderData or Redux state
      let cartItems = [];
      
      // 1. Try from orderData first
      if (orderData.items && Array.isArray(orderData.items) && orderData.items.length > 0) {
        cartItems = orderData.items;
        console.log('📦 Got cart items from orderData:', cartItems);
      } 
      // 2. Try from localStorage
      else {
        try {
          const stored = localStorage.getItem('cartItems');
          if (stored) {
            cartItems = JSON.parse(stored);
            console.log('📦 Got cart items from localStorage:', cartItems);
          }
        } catch (e) {
          console.warn('Could not parse cart items from localStorage');
        }
      }
      
      // 3. If still no items, try to get from Redux persisted state
      if (!cartItems || cartItems.length === 0) {
        try {
          const reduxState = sessionStorage.getItem('persist:root');
          if (reduxState) {
            const parsed = JSON.parse(reduxState);
            if (parsed.cart) {
              const cartData = JSON.parse(parsed.cart);
              if (cartData.items && cartData.items.length > 0) {
                cartItems = cartData.items;
                console.log('📦 Got cart items from Redux state:', cartItems);
              }
            }
          }
        } catch (e) {
          console.warn('Could not get cart from Redux state');
        }
      }
      
      // Format items for the API
      const formattedItems = cartItems.map(item => ({
        item_id: parseInt(item.item_id) || parseInt(item.id) || 0,
        quantity: parseInt(item.quantity) || 1,
        price: parseFloat(item.price) || 0,
        item_type: item.item_type || 'product'
      }));
      
      // Calculate totals
      const subtotal = formattedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      const gst = subtotal * 0.18;
      const total = subtotal + gst;
      
      // ✅ Build complete payload with ALL required fields
      const payload = {
        payment_method: orderData.payment_method || 'cash',
        customer_address: orderData.customer_address || orderData.address || '',
        customer_phone: orderData.customer_phone || orderData.phone || '',
        customer_name: orderData.customer_name || orderData.name || '',
        items: formattedItems,        // ✅ CRITICAL: Include cart items
        subtotal: subtotal,
        gst: gst,
        total: total,
        delivery_charge: orderData.delivery_charge || 0
      };
      
      // Add optional fields
      if (orderData.latitude) payload.latitude = orderData.latitude;
      if (orderData.longitude) payload.longitude = orderData.longitude;
      if (orderData.user_id) payload.user_id = parseInt(orderData.user_id);
      
      console.log('🔄 POST - Complete order payload:', JSON.stringify(payload, null, 2));
      console.log('🔄 POST - Items count:', formattedItems.length);
      
      // Check if cart is empty
      if (formattedItems.length === 0) {
        throw new Error('Your cart is empty. Please add items before placing an order.');
      }
      
      const response = await this.api.post('/checkout');
      console.log('✅ POST - Order placed:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ POST - Place order error:', error);
      
      // Log detailed validation errors for debugging
      if (error.response) {
        console.error('⚠️ Error status:', error.response.status);
        console.error('⚠️ Error data:', error.response.data);
        
        // If 422, get the validation errors
        if (error.response.status === 422) {
          const errorData = error.response.data;
          let errorMessage = 'Validation failed:\n';
          
          if (errorData.errors) {
            Object.keys(errorData.errors).forEach(field => {
              errorMessage += `- ${field}: ${errorData.errors[field].join(', ')}\n`;
            });
            throw new Error(errorMessage);
          } else if (errorData.message) {
            throw new Error(errorData.message);
          }
        }
      }
      
      throw error;
    }
  };

  // ✅ GET - Order History
  getOrderHistory = async () => {
    try {
      console.log('🔄 GET - Fetching order history...');
      const response = await this.api.get('/orders/history');
      console.log('✅ GET - Order history fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ GET - Order history error:', error);
      throw error;
    }
  };
}

const apiService = new ApiService();
export default apiService;